import "draft-js-mention-plugin/lib/plugin.css";
import * as React from "react";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { messagesAuditEvents } from "~/AIEvents/messagesAuditEvents";
import { useTrackEvent } from "~/common/audit";
import { Team, User } from "~/normalizr/entities";
import { loadAvailableUsersRequest, loadTeamsRequest } from "~/Redux/AvailableUsers";
import { loadProjectMembers } from "~/Redux/ProjectMembers/actions/loadProjectMembers";
import { getProjectUserMembers } from "~/Redux/ProjectMembers/selectors";
import { getSpaceMembersByProject, loadSpaceMembersByProject } from "~/Redux/SpaceMembers";
import { useIsOpened } from "~/sharedComponents/advancedEditor/hooks/useIsOpened";
import { useUpdatePositionOnScroll } from "~/sharedComponents/advancedEditor/hooks/useUpdatePositionOnScroll";
import { Mention } from "~/sharedComponents/advancedEditor/types/types";
import { useReduxAction } from "~/sharedComponents/hooks/useReduxAction";
import { loadMentions } from "../actions/mentions";
import { usePreventDetailsClosingOnEsc } from "../hooks/usePreventDetailsClosingOnEsc";
import { useSuggestionsFroMessageBoard } from "../hooks/useSuggestionsFroMessageBoard";
import { getMentions, getTeamMentions } from "../selectors/mentions";
import { MentionPlugin, MessageOwnerState, RootMessageBoardState } from "../types";
import { scrollSelectionIntoView } from "../utils/scrollSelectionIntoView";
import { MentionEntry } from "./MentionEntry";

type MentionSuggestionsInnerProps = {
    recipients: User[];
    projectMembers: number[];
    spaceMembers: number[];
    teams: Team[];
} & MentionPlugin;

const MentionSuggestionsInner = ({
    recipients,
    plugin,
    projectMembers,
    spaceMembers,
    teams,
}: MentionSuggestionsInnerProps) => {
    const track = useTrackEvent();
    const { isOpened, onOpen, onClose } = useIsOpened();
    useUpdatePositionOnScroll(isOpened);

    const { suggestions, onSearchChange } = useSuggestionsFroMessageBoard(
        recipients,
        isOpened,
        projectMembers,
        spaceMembers,
        teams
    );

    const containerRef = usePreventDetailsClosingOnEsc(isOpened);

    // allows scroll suggestions popup by dragging mouse (otherwise suggestion
    // popup will be closed on scrollbar mousedown)
    const preventEditorBlur = (e: React.MouseEvent) => e.preventDefault();

    // scroll message board to added mention if text was moved to new line
    // see bug 39779 Messageboard window isn't scrolled to mention if it moves to the next line
    const onAddMention = React.useCallback((mention: Mention) => {
        track({ eventName: messagesAuditEvents.mentionAdded, details: mention.teamId ? "team" : "user" });
        requestAnimationFrame(() => scrollSelectionIntoView());
    }, []);

    const { MentionSuggestions } = plugin;

    return (
        <div onMouseDown={preventEditorBlur} ref={containerRef}>
            <MentionSuggestions
                unselectable={"on"}
                onSearchChange={onSearchChange}
                suggestions={suggestions}
                entryComponent={MentionEntry}
                onOpen={onOpen}
                onClose={onClose}
                onAddMention={onAddMention}
            />
        </div>
    );
};

type MentionSuggestionsWrapperProps = MentionPlugin &
    MessageOwnerState & {
        isFocused: boolean;
    };

export const MentionSuggestionsWrapper = ({
    plugin,
    projectId,
    projectOrTask,
    isFocused,
}: MentionSuggestionsWrapperProps) => {
    const loadMentionsAction = useReduxAction(loadMentions);
    const loadUsers = useReduxAction(loadAvailableUsersRequest);
    const loadTeams = useReduxAction(loadTeamsRequest);
    const mentions = useSelector((state: RootMessageBoardState) => getMentions(state, projectOrTask));
    const teams = useSelector((state: RootMessageBoardState) => getTeamMentions(state, projectOrTask));

    useEffect(() => {
        loadUsers({});
        loadTeams();
    }, []);

    useEffect(() => {
        loadMentionsAction({ ...projectOrTask, forceReload: true });
    }, [projectOrTask]);

    const { projectMembers, spaceMembers } = useLoadProjectMembers(isFocused, projectId);

    return (
        <MentionSuggestionsInner
            plugin={plugin}
            recipients={mentions}
            projectMembers={projectMembers}
            spaceMembers={spaceMembers}
            teams={teams}
        />
    );
};

const useLoadProjectMembers = (isFocused: boolean, projectId: number) => {
    const dispatch = useDispatch();
    // start load project members only when editor is focused first time
    // because otherwise we'll load them everytime user opens project/task details even if user
    // isn't going to post any message
    const isFocusedOnce = useRef(false);
    useEffect(() => {
        if (isFocused && !isFocusedOnce.current) {
            isFocusedOnce.current = true;

            dispatch(loadProjectMembers({ projectId }));
            dispatch(loadSpaceMembersByProject({ projectId }));
        }
    }, [isFocused]);

    const projectMembers = useSelector((state: Dictionary<number[]>) => getProjectUserMembers(state, projectId));
    const spaceMembers = useSelector(getSpaceMembersByProject(projectId));

    return { projectMembers, spaceMembers };
};
